import { useEffect, useState } from "react";
import {
  BOOKING_CHANNEL_LABELS,
  isOnlineBooking,
  type AppointmentEvent,
  type BookingChannel,
  type ClientStatus,
} from "@shared/modules/x-atelier";
import { Badge } from "../../../core/components/ui/badge";
import { formatDateTime } from "../../../core/lib/format";
import { listAppointmentEvents } from "../lib/api";

/**
 * Provenance d'un rendez-vous, distincte de son statut : « Réservé en ligne »
 * (site ou assistant du site) et « Léo » (téléphone). Rien pour le CRM.
 * `compact` : pastille courte pour l'agenda et les rangées.
 */
export function BookingChannelBadge({
  channel,
  compact = false,
}: {
  channel?: BookingChannel | null;
  compact?: boolean;
}) {
  if (!channel) return null;
  if (isOnlineBooking(channel))
    return compact ? (
      <span className="channel-flag channel-flag--online" title={BOOKING_CHANNEL_LABELS[channel]}>
        En ligne
      </span>
    ) : (
      <span title={BOOKING_CHANNEL_LABELS[channel]}>
        <Badge variant="info">Réservé en ligne</Badge>
      </span>
    );
  if (channel === "appel_ia")
    return compact ? (
      <span className="channel-flag" title={BOOKING_CHANNEL_LABELS[channel]}>
        Léo
      </span>
    ) : (
      <span title={BOOKING_CHANNEL_LABELS[channel]}>
        <Badge variant="neutral">Réservé par Léo</Badge>
      </span>
    );
  return null;
}

/** Le client a confirmé sa présence (au téléphone avec Léo, ou noté au CRM). */
export function PresenceBadge({
  at,
  compact = false,
}: {
  at?: number | string | null;
  compact?: boolean;
}) {
  if (!at) return null;
  const when = formatDateTime(at, { dateStyle: "medium", timeStyle: "short" });
  return compact ? (
    <span className="channel-flag channel-flag--presence" title={`Présence confirmée le ${when}`}>
      Présence ✓
    </span>
  ) : (
    <span title={`Confirmée le ${when}`}>
      <Badge variant="success">Présence confirmée</Badge>
    </span>
  );
}

/**
 * Client sans aucun travail terminé (première visite) : « Nouveau ». Il
 * devient confirmé de lui-même au premier travail terminé.
 */
export function NewClientBadge({
  status,
  compact = false,
}: {
  status?: ClientStatus | null;
  compact?: boolean;
}) {
  if (status !== "nouveau") return null;
  return compact ? (
    <span
      className="channel-flag channel-flag--new"
      title="Nouveau client : aucun travail terminé pour l’instant"
    >
      Nouveau
    </span>
  ) : (
    <span title="Aucun travail terminé pour l’instant : le client devient confirmé au premier travail terminé.">
      <Badge variant="warning">Nouveau client</Badge>
    </span>
  );
}

/** Statut sur la fiche client : nouveau, ou confirmé depuis son premier travail terminé. */
export function ClientStatusBadge({
  status,
  confirmedAt,
}: {
  status?: ClientStatus | null;
  confirmedAt?: number | string | null;
}) {
  if (status === "nouveau") return <NewClientBadge status="nouveau" />;
  if (status !== "confirme") return null;
  const since = confirmedAt ? formatDateTime(confirmedAt, { dateStyle: "long" }) : null;
  return (
    <span title={since ? `Premier travail terminé le ${since}` : undefined}>
      <Badge variant="success">Client confirmé</Badge>
    </span>
  );
}

/** Journal du rendez-vous : création et canal, présence, dérogations, demande d'origine. */
export function AppointmentJournal({
  appointmentId,
  version,
}: {
  appointmentId: string;
  version?: unknown;
}) {
  const [events, setEvents] = useState<AppointmentEvent[] | null>(null);
  useEffect(() => {
    let alive = true;
    listAppointmentEvents(appointmentId)
      .then((list) => alive && setEvents(list))
      .catch(() => alive && setEvents([]));
    return () => {
      alive = false;
    };
  }, [appointmentId, version]);
  if (!events) return <p className="travail__empty">Chargement…</p>;
  if (events.length === 0)
    return (
      <p className="travail__empty">Aucun événement consigné (rendez-vous antérieur au journal).</p>
    );
  return (
    <ol className="journal">
      {events.map((e) => (
        <li key={e.id} className={`journal__item journal__item--${e.kind}`}>
          <span className="journal__when">
            {formatDateTime(e.createdAt, { dateStyle: "medium", timeStyle: "short" })}
          </span>
          <span className="journal__text">{e.description}</span>
          {e.actorName ? <span className="journal__actor">{e.actorName}</span> : null}
        </li>
      ))}
    </ol>
  );
}
